/**
 * @fileoverview 資料LP・動画LP・イベントLP（CTAフォームを持つ1カラムのページ）用テンプレート
 * @description TemplateDefault と違い、グローバルナビ・検索フォーム・タイムライン・チャットボット・
 *              2カラム（aside）を持たない。持たないものを props で殺すのではなく、
 *              このテンプレート自体が1カラム構成として独立している。
 * @description プレビュー画面（pages/preview/**）も公開ページと同じページコンポーネントを通すため、
 *              プレビューのときだけ main の先頭に告知帯を出す
 */
<script lang="ts">
import {
  defineComponent,
  PropType,
  computed,
  inject,
  useSlots,
  onMounted,
} from 'vue'
import HeaderGlobal from '@/components/_core/HeaderGlobal.vue'
import FooterGlobal from '@/components/_core/FooterGlobal.vue'
import MediaQueryObserver from '@/components/_core/MediaQueryObserver.vue'
import LoadingScreen from '@/components/loading/LoadingScreen.vue'
import ToastMessage from '@/components/toast/ToastMessage.vue'
import ValidationInspector from '@/components/validation/ValidationInspector.vue'
import PreviewNotice from '@/components/preview/PreviewNotice.vue'
import ScrollToPageTop from './ScrollToPageTop.vue'
import { IS_PREVIEW } from '@/constants/injectionKeys'
import { useHashLinkHandler } from '@/composables/useHashLinkHandler'
import { useHeaderStore } from '@/stores/header'
import { useScreenLockStore } from '@/stores/screenLock'
import { useClientStore } from '@/stores/client'

export default defineComponent({
  name: 'TemplateSolutionFormLp',

  components: {
    HeaderGlobal,
    FooterGlobal,
    MediaQueryObserver,
    LoadingScreen,
    ToastMessage,
    ValidationInspector,
    PreviewNotice,
    ScrollToPageTop,
  },

  props: {
    /**
     * ヘッダーに表示されるロゴの色
     */
    logoColor: {
      type: String as PropType<'default' | 'white' | 'gray'>,
      default: 'default',
    },
  },

  setup() {
    // store
    const headerStore = useHeaderStore()
    const screenLockStore = useScreenLockStore()
    const clientStore = useClientStore()

    const slots = useSlots()
    const hasBreadcrumbHeader = computed(() => !!slots.breadcrumbHeader)
    const { registerHashLinkClickHandler } = useHashLinkHandler()

    /**
     * プレビュー表示かどうか
     * @description プレビュー画面のページコンポーネント（pages/preview/**）が provide する。
     *              プレビューかどうかは着地した URL で確定しており（URL パスからページコンポーネントが
     *              解決される。_core/pageSelector.ts）、同一ページ内で分岐する類の値ではないため、
     *              サーバから受け取らずページ側から渡す。
     *              公開ページでは provide されないため既定の false になり、公開ページの App.vue は
     *              プレビューを一切知らないままでいられる
     */
    const isPreview = inject(IS_PREVIEW, false)

    onMounted(() => {
      if (!slots.main) {
        console.error('This page doesn\'t have main contents. Please inject a main slot into App.')
      }

      clientStore.setIsTouchDevice()

      /**
       * テンプレート側で共通的に同一ページへのハッシュリンクの制御をイベントリスナに登録する
       * 同一ページ内でのハッシュリンクをクリックした際に、ブラウザでのページ遷移ではなく、スクリプトでのスムーススクロールで遷移させる
       */
      registerHashLinkClickHandler({ withInitialScroll: true })
    })

    // ヘッダーは常に描画するため、ToastMessage 等が参照する表示状態を立てておく
    headerStore.updateShowStatus(true)

    return {
      // store
      screenLockStore,
      clientStore,

      // data
      isPreview,

      // computed
      hasBreadcrumbHeader,
    }
  },
})
</script>

<template>
  <div
    class="f-container"
    :data-app="clientStore.context.isApp"
    :data-screenlock="screenLockStore.isLock ? 'true' : 'false'"
    :data-backdrop-darken="screenLockStore.isBlackOut"
    :data-backdrop-blur="screenLockStore.isBlur">
    <div
      id="global-container"
      class="f-container--body">
      <HeaderGlobal
        v-if="!clientStore.context.isApp"
        :has-global-navigation="false"
        :has-search-form="false"
        :logo-color="logoColor" />

      <section
        class="f-section__contents"
        :data-app="clientStore.context.isApp">
        <div
          v-if="hasBreadcrumbHeader"
          class="f-section__contents--body is-breadcrumb__header">
          <slot name="breadcrumbHeader" />
        <!-- /.f-section__contents body --></div>

        <div class="f-section__contents--body is-primary">
          <main class="main">
            <PreviewNotice v-if="isPreview" />
            <slot name="main" />
          </main>
        <!-- /.f-section__contents body --></div>
      <!-- /.f-section__contents --></section>

      <template v-if="!clientStore.context.isApp">
        <slot name="footer">
          <FooterGlobal :has-follow-sns="false" />
        </slot>
      </template>

      <!-- overlay contents -->
      <ScrollToPageTop />
      <LoadingScreen :is-show="screenLockStore.showWaitAnimation" />
      <ToastMessage />
      <MediaQueryObserver />
      <ValidationInspector />
    <!-- /.f-container body --></div>
  <!-- /.f-container --></div>
</template>

<style lang="sass" scoped>
// NOTE: TemplateDefault / TemplateLite / TemplateSignUp と重複する基底スタイル。
//       共通パーシャルへの切り出しは別リファクタで行うため、ここでは TemplateDefault の値を複製している
$global-top-gutter-px: 20
.f-container
  $root: &
  width: 100%
  height: 100%
  min-height: 100%

  &::before
    content: ''
    +transition

  &[data-screenlock="true"]
    &::before
      width: 100%
      height: 100%
      display: block
      position: fixed
      top: 0
      left: 0
      z-index: 20

    &[data-backdrop-darken="true"]::before
      background-color: rgba($bg-black, 0.56)

    // スクリーンロック時に「ぼかし」を入れる場合
    &[data-backdrop-blur="true"]::before
      -webkit-backdrop-filter: blur(6px)
      backdrop-filter: blur(6px)

    &:not([data-backdrop-darken="true"])[data-backdrop-blur="true"]::before
      background-color: rgba($bg-white, 0.3)

  & &--body
    min-height: 100%
    display: flex
    flex-direction: column
    // グローバルナビを持たないテンプレートなので、ヘッダーは primary 段のみの高さになる
    padding-top: #{$header-primary-height-pc}px
    background-color: rgba($bg-x-light-gray, 1)

  &[data-app="true"]
    #{$root}--body
      padding-top: 0

  +breakpoint($max-media: tablet)
    #{$root}--body
      padding-top: #{$header-primary-height-sp}px

.f-section__contents
  $root: &
  width: 100%
  max-width: #{$global-width-pc}px
  flex-grow: 1
  margin: 0 auto
  padding:
    top: changeSizePx2Em($global-top-gutter-px)
    bottom: 1em
    left: #{$global-prop-pc}px
    right: #{$global-prop-pc}px
  +transition

  & &--body
    width: 100%

    &.is-breadcrumb__header
      margin-bottom: 1.2em

    &.is-primary
      margin-bottom: changeSizePx2Em(60)

  // アプリ版
  &[data-app="true"]
    #{$root}--body
      &.is-breadcrumb__header
        margin-top: 0

      &.is-primary
        margin-bottom: 0

  +breakpoint($max-media: tablet)
    #{$root}--body
      &.is-breadcrumb__header
        margin:
          top: -0.2em
          bottom: 1.2em

  +breakpoint
    padding:
      left: 0
      right: 0

    #{$root}--body
      &.is-breadcrumb__header
        display: none

      &.is-primary
        margin-bottom: 0
        padding: 0 #{$global-prop-sp}px
</style>
